import { headerAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import { exactUrlToRegex } from "../urlPatternConversion";
import logo from "./logo.svg";

export const cobalt = defineConnector({
  identifier: "cobalt",
  name: "Cobalt",
  tagLine: "Pentest programs and security findings",
  logo,
  protocol: "http",
  tags: ["security"],
  defaultUrlPattern: ({ baseUrl }) =>
    exactUrlToRegex(baseUrl || "https://api.us.cobalt.io"),
  auth: headerAuth([
    {
      name: "Authorization",
      value: {
        from: "template",
        template: "Bearer {secrets.personalApiToken}",
      },
    },
    {
      name: "Accept",
      value: { from: "literal", value: "application/vnd.cobalt.v2+json" },
    },
    {
      name: "X-Org-Token",
      value: { from: "secret", field: "organizationToken" },
    },
  ]),
  steps: [
    {
      title: "Set up Cobalt",
      items: [
        {
          kind: "markdown",
          content:
            "In your Cobalt profile, create a personal API token. Use the [Cobalt API](https://cobalt-public-api.netlify.app/v2/) to call **GET /orgs** and copy the target organization’s token. Enter the personal and organization tokens below. Use **https://api.us.cobalt.io** for the US region or **https://api.eu.cobalt.io** for the EU region.",
        },
      ],
    },
    {
      title: "Enter connection details",
      items: [
        {
          kind: "url",
          label: "API base URL",
          required: true,
          defaultValue: "https://api.us.cobalt.io",
          target: {
            kind: "configuration",
            field: "baseUrl",
          },
          helperText:
            "Select the API host for your region: api.us.cobalt.io or api.eu.cobalt.io.",
        },
        {
          kind: "input",
          label: "Personal API token",
          required: true,
          target: {
            kind: "secrets",
            field: "personalApiToken",
          },
          helperText:
            "Create a personal Cobalt API token from the Cobalt profile. It is sent as the Bearer token in the Authorization header.",
        },
        {
          kind: "input",
          label: "Organization token",
          required: true,
          target: {
            kind: "secrets",
            field: "organizationToken",
          },
          helperText:
            "Use GET /orgs with the personal API token, then copy the target organization’s token value. Send it as X-Org-Token for organization-scoped requests.",
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ baseUrl }) => ({
      url:
        (baseUrl || "https://api.us.cobalt.io").trim().replace(/\/+$/, "") +
        "/pentests?limit=1",
      ...{
        method: "GET",
        headers: { Accept: "application/vnd.cobalt.v2+json" },
      },
    }),
  },
});
